import React, { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Chip,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { Scaffold } from "../Scaffold";
import LoadingSpinner from "../LoadingSpinner";
import { State } from "../redux/types";
import { useSelector, useDispatch } from "react-redux";
import { dateFormatter, maskPhone } from "../utils/masking";
import { ConfirmActionDialog } from "../components/ConfirmActionDialog";
import { VendorUpdateDialog } from "../components/VendorUpdateDialog";
import { useVendorActions } from "../hooks/useVendorActions";
import { vendorRequest } from "../api/api";
import { setAdvanceResult } from "../redux/actions";

interface DeliveryAddress {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  favorite?: boolean;
  [key: string]: unknown;
}

const VENDOR_KEY = "cdp";

type Customer = {
  firstName?: string;
  lastName?: string;
  email?: string;
  mobilePhoneNumber?: string;
  emailOptIn?: boolean;
  birthDate?: string;
  birthDateUpdated?: boolean;
  registrationDate?: string;
  termsAcceptedDate?: boolean;
  phoneVerified?: boolean;
  favoriteStoreId?: string;
  totalVisits?: number;
  totalVisitsLifetime?: string | number;
  registrationSource?: string;
};

function DetailRow({ label, value }: Readonly<{ label: string; value: React.ReactNode }>) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Typography variant="body2" sx={{ fontWeight: "bold" }}>{label}</Typography>
      <Typography variant="body2" sx={{ textAlign: "right" }}>{value}</Typography>
    </Box>
  );
}

function CDPCustomerDetails({ customer, isAccountEnabled }: Readonly<{ customer?: Customer | null; isAccountEnabled: boolean }>) {
  if (!customer) {
    return null;
  }
  return (
    <Stack direction="column" spacing={2}>
      <Stack spacing={1.5}>
        <DetailRow label="Name:" value={`${customer.firstName ?? ""} ${customer.lastName ?? ""}`.trim() || "N/A"} />
        <DetailRow label="Email:" value={customer.email ?? "N/A"} />
        <DetailRow label="Email opt in:" value={customer.emailOptIn ? "Yes" : "No"} />
        <DetailRow label="Phone:" value={customer.mobilePhoneNumber ? maskPhone(customer.mobilePhoneNumber) : "N/A"} />
        <DetailRow label="Birth Date:" value={customer.birthDate ? dateFormatter(customer.birthDate) : "N/A"} />
        <DetailRow label="Birth Date Updated:" value={customer.birthDateUpdated ? "Yes" : "No"} />
        <DetailRow label="Registration Date:" value={customer.registrationDate ? dateFormatter(customer.registrationDate) : "N/A"} />
        <DetailRow label="Terms Accepted Date:" value={customer.termsAcceptedDate ? "Yes" : "No"} />
        <DetailRow
          label="Zaxby Account Status:"
          value={
            <Typography component="span" sx={{ color: isAccountEnabled ? "success.main" : "error.main", fontWeight: "bold" }}>
              {isAccountEnabled ? "Enabled" : "Disabled"}
            </Typography>
          }
        />
      </Stack>
      <Stack spacing={1.5}>
        <DetailRow label="Phone Verified CDP:" value={customer.phoneVerified ? "Yes" : "No"} />
        <DetailRow label="Favorite Store ID:" value={customer.favoriteStoreId || "N/A"} />
        <DetailRow label="Total Visits:" value={customer.totalVisits ?? 0} />
        <DetailRow label="Total Visits Lifetime:" value={customer.totalVisitsLifetime ?? "N/A"} />
        <DetailRow label="Registration Source:" value={customer.registrationSource || "N/A"} />
      </Stack>
    </Stack>
  );
}

export const CDP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const advanceVendorResult = useSelector((state: State) => state.advanceResult);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get customerVendorId from navigation state (passed from AdvanceResult)
  const locationState = location.state as { customerVendorId?: string } | null;

  const userDeliveryAddressList: DeliveryAddress[] = advanceVendorResult?.customer?.userDeliveryAddressList ?? [];
  const cdpId = advanceVendorResult?.customer?.cdpId
    ?? advanceVendorResult?.customer?.customerVendorId
    ?? advanceVendorResult?.customer?.id
    ?? advanceVendorResult?.value?.cdpId
    ?? advanceVendorResult?.value?.customerVendorId
    ?? advanceVendorResult?.value?.id
    ?? locationState?.customerVendorId
    ?? "";

  const handleBackToResults = useCallback(() => {
    navigate("/advanceresult");
  }, [navigate]);

  const isAccountEnabled =
    advanceVendorResult?.customer?.cognitoEnabled === "true" ||
    advanceVendorResult?.customer?.cognitoEnabled === true;

  // Refresh vendor data after successful action
  const handleActionSuccess = useCallback(async (actionKey: string) => {
    if (actionKey === "delete") {
      navigate("/advanceresult");
      return;
    }

    if (!cdpId) { return; }

    setIsRefreshing(true);
    try {
      await new Promise<void>(resolve => { setTimeout(resolve, 1000); });
      const response = await vendorRequest(cdpId, VENDOR_KEY);
      if (response) {
        dispatch(setAdvanceResult({ ...response }));
      }
    } catch (error) {
      console.debug("CDP vendor refetch failed", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [cdpId, dispatch, navigate]);

  // ============================================
  // Vendor Actions (UPDATE, DELETE, DNSS, DISABLE/ENABLE)
  // All use the vendor-specific endpoint with cdpId
  // ============================================

  const {
    dialogOpen: vendorDialogOpen,
    updateDialogOpen: vendorUpdateDialogOpen,
    currentAction: vendorCurrentAction,
    loading: vendorLoading,
    openDialog: openVendorDialog,
    closeDialog: closeVendorDialog,
    closeUpdateDialog: closeVendorUpdateDialog,
    confirmAction: confirmVendorAction,
    confirmUpdate: confirmVendorUpdate,
  } = useVendorActions({
    vendorKey: VENDOR_KEY,
    customerVendorId: cdpId,
    vendorName: "CDP",
    isAccountEnabled,
    onSuccess: handleActionSuccess,
    onFailure: () => {
      // Handle failure if needed
    },
  });

  const buttonStyle = {
    backgroundColor: "#041E42",
    color: "white",
    px: 3,
    py: 1,
    textTransform: "none" as const,
    "&:hover": {
      backgroundColor: "#0a3d7a",
    },
  };

  if (!advanceVendorResult) {
    return (
      <Scaffold navBar center>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <LoadingSpinner />
        </Box>
      </Scaffold>
    );
  }

  return (
    <Scaffold navBar center>
      {isRefreshing && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            bgcolor: "rgba(255,255,255,0.7)",
          }}
        >
          <LoadingSpinner />
        </Box>
      )}
      {/* Back Button - Fixed to top left */}
      <Box
        sx={{
          position: "fixed",
          top: 80,
          left: 20,
          zIndex: 10,
          mt: "2rem",
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBackToResults}
          sx={{
            color: "text.secondary",
            textTransform: "none",
            fontSize: "0.875rem",
          }}
        >
          BACK TO RESULTS
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        {/* CDP Title - Centered */}
        <Typography
          variant="h4"
          sx={{ mb: 3, fontWeight: "bold", textAlign: "center", width: "100%" }}
        >
          CDP
        </Typography>

        {/* Customer Account Card */}
        <Card 
            sx={{ 
              border: "3px solid rgba(0,0,0,0.1)", 
              borderRadius: 2,
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              minWidth: "400px",
              maxWidth: "600px"
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3, 
                  fontWeight: "bold",
                  color: "#333"
                }}
              >
                Customer Account
              </Typography>

              {advanceVendorResult ? (
                <CDPCustomerDetails customer={advanceVendorResult.customer} isAccountEnabled={isAccountEnabled} />
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No customer data available
                </Typography>
              )}

              {/* Action Buttons */}
              <Box sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  sx={buttonStyle}
                  onClick={() => openVendorDialog("update")}
                >
                  UPDATE
                </Button>
                
                <Button
                  variant="contained"
                  sx={{ ...buttonStyle, backgroundColor: "#b71c1c", "&:hover": { backgroundColor: "#7f0000" } }}
                  onClick={() => openVendorDialog("delete")}
                >
                  DELETE
                </Button>
              </Box>
            </CardContent>
          </Card>
      </Box>

      {/* Delivery Addresses Dialog */}
        <Dialog 
          open={addressDialogOpen} 
          onClose={() => setAddressDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>All Delivery Addresses</DialogTitle>
          <DialogContent>
            <List>
              {userDeliveryAddressList.map((address) => (
                <ListItem
                  key={`${address.addressLine1 ?? ""}-${address.city ?? ""}-${address.postalCode ?? ""}-${String(address.favorite)}`}
                  sx={{ 
                    border: "1px solid #e0e0e0", 
                    borderRadius: 1, 
                    mb: 1,
                    backgroundColor: address.favorite ? "#e3f2fd" : "transparent"
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body1">
                          {address.addressLine1 || "No street address"}
                        </Typography>
                        {address.favorite && (
                          <Chip label="Favorite" size="small" color="primary" sx={{ height: 20, fontSize: "0.7rem" }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {[address.addressLine2, address.city, address.state, address.postalCode]
                          .filter(Boolean)
                          .join(", ") || "No additional details"}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddressDialogOpen(false)} variant="outlined" color="secondary">Close</Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog for Vendor Actions (DELETE, DNSS, DISABLE/ENABLE) */}
        <ConfirmActionDialog
          open={vendorDialogOpen}
          actionLabel={vendorCurrentAction?.label ?? ""}
          description={vendorCurrentAction?.description ?? ""}
          confirmText={vendorCurrentAction?.confirmText}
          loading={vendorLoading}
          onConfirm={confirmVendorAction}
          onCancel={closeVendorDialog}
        />

        {/* Vendor Update Dialog with Form */}
        <VendorUpdateDialog
          open={vendorUpdateDialogOpen}
          vendorName="CDP"
          loading={vendorLoading}
          initialValues={{
            firstName: advanceVendorResult?.customer?.firstName,
            lastName: advanceVendorResult?.customer?.lastName,
            email: advanceVendorResult?.customer?.email,
            phoneNumber: advanceVendorResult?.customer?.mobilePhoneNumber,
            dateOfBirth: advanceVendorResult?.customer?.birthDate,
          }}
          onConfirm={confirmVendorUpdate}
          onCancel={closeVendorUpdateDialog}
        />
    </Scaffold>
  );
};
